import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi } from "@/features/auth/api";
import { useAuthStore } from "@/features/auth/store";
import PasswordStrength, {
  evaluatePassword,
} from "@/features/auth/components/PasswordStrength";

type RegisterErr = {
  error?: string | { code?: string; message?: string };
  non_field_errors?: string[];
  company_name?: string[];
  email?: string[];
  first_name?: string[];
  last_name?: string[];
  password?: string[];
  password_confirm?: string[];
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  // Invite-only platforms don't offer self sign-up — the backend refuses
  // registration too; this just replaces the dead form with an explanation.
  const configQ = useQuery({
    queryKey: ["auth", "config"],
    queryFn: authApi.config,
    staleTime: 10 * 60_000,
  });

  const [form, setForm] = useState({
    company_name: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((p) => ({ ...p, [key]: "" }));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (form.password !== form.password_confirm) {
      setError("Passwords do not match");
      return;
    }

    // Mirror the server's password rules so the user gets instant feedback
    // instead of a round-trip rejection.
    const pw = evaluatePassword(form.password, {
      email: form.email,
      firstName: form.first_name,
      lastName: form.last_name,
    });
    if (!pw.valid) {
      setFieldErrors({ password: "Password doesn't meet the requirements below." });
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.register(form);
      if (res?.user) setUser(res.user);
      toast.success("Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const data = (err as AxiosError<RegisterErr>)?.response?.data;
      if (data) {
        const fe: Record<string, string> = {};
        let hasGeneral = false;
        for (const key of Object.keys(data) as (keyof RegisterErr)[]) {
          const value = data[key];
          if (key === "error") {
            const e =
              typeof value === "string"
                ? value
                : (value as { message?: string })?.message;
            setError(e || "Registration failed");
            hasGeneral = true;
          } else if (
            key === "non_field_errors" &&
            Array.isArray(value)
          ) {
            setError((value as string[]).join(" "));
            hasGeneral = true;
          } else if (Array.isArray(value)) {
            fe[key] = (value as string[]).join(" ");
          } else if (typeof value === "string") {
            fe[key] = value;
          }
        }
        if (Object.keys(fe).length) setFieldErrors(fe);
        if (!hasGeneral && !Object.keys(fe).length)
          setError("Registration failed");
      } else {
        setError("Registration failed");
      }
    } finally {
      setLoading(false);
    }
  }

  if (configQ.data && !configQ.data.public_signup_enabled) {
    return (
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Sign-up is by invitation
        </h1>
        <p className="text-sm text-muted-foreground">
          Accounts on this platform are set up for you. Please contact us to
          get started, or sign in if you already have an account.
        </p>
        <p className="text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="text-primary underline-offset-4 hover:underline"
          >
            Go to sign in
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm space-y-6">
      <div className="space-y-1 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Create Account
        </h1>
        <p className="text-sm text-muted-foreground">
          Create your workspace and get started.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <Field
          id="company_name"
          label="Company / Organization Name"
          value={form.company_name}
          onChange={(v) => update("company_name", v)}
          error={fieldErrors.company_name}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            id="first_name"
            label="First name"
            value={form.first_name}
            onChange={(v) => update("first_name", v)}
            error={fieldErrors.first_name}
          />
          <Field
            id="last_name"
            label="Last name"
            value={form.last_name}
            onChange={(v) => update("last_name", v)}
            error={fieldErrors.last_name}
          />
        </div>

        <Field
          id="email"
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => update("email", v)}
          error={fieldErrors.email}
          required
        />

        <Field
          id="password"
          label="Password"
          type="password"
          value={form.password}
          onChange={(v) => update("password", v)}
          error={fieldErrors.password}
          required
        />

        <PasswordStrength
          password={form.password}
          email={form.email}
          firstName={form.first_name}
          lastName={form.last_name}
        />

        <Field
          id="password_confirm"
          label="Confirm password"
          type="password"
          value={form.password_confirm}
          onChange={(v) => update("password_confirm", v)}
          error={fieldErrors.password_confirm}
          required
        />

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Creating…" : "Create Account"}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link
          to="/login"
          className="text-primary underline-offset-4 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = "text",
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        aria-invalid={!!error}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
